import { Component, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { merge, of, Subscription, combineLatest, Observable, BehaviorSubject, forkJoin } from 'rxjs';
import { BreadcrumbService, IBreadcrumb } from '../shared/breadcrumb-service/breadcrumb.service';
import { UrlBuilder } from '../../core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { ReportData, GridFilter, ReportRequestInfo } from './interfaces/report-data';
import { GridDetails } from '../tracking-overview/interfaces/grid';
import { ReportColumnsService } from './services/report-columns-service';
import { ReportsStorageService } from './services/reports-storage-service';
import { ActivityLogger } from '../../activities/activity-logger';
import { UserActionName } from '../../activities/user-action';
import { cloneDeep } from 'lodash';
import { TrackingGridService } from '../../shared/covance-grid/tracking-grid.service';

@Component({
    selector: 'sample-tracking-reports',
    template: require('./sample-tracking-reports.component.html'),
    styles: [require('./sample-tracking-reports.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SampleTrackingReportsComponent implements OnDestroy {

    public generatedReportDetails: GridDetails = null;
    public generatedReportData: ReportData[];
    public searchTerm = new FormControl('');
    public loadingComplete = false;
    public loadingScheduledReports = false;
    public isGeneral = false;
    public isProtocol = false;
    public reportFilter: GridFilter;

    public scheduledReportDetails: GridDetails = null;
    public scheduledReportData: ReportData[];
    private searchSubscription: Subscription;
    private actionName: UserActionName = 'View Reports';

    constructor(
        private breadCrumbService: BreadcrumbService,
        private urlBuilder: UrlBuilder,
        private changeDetectorRef: ChangeDetectorRef,
        private reportsColumnService: ReportColumnsService,
        private reportsStorageService: ReportsStorageService,
        private activityLogger: ActivityLogger,
        private trackingGridService: TrackingGridService,
    ) {

        this.getActivatedRouteType();
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );
        this.loadReports();

        this.searchSubscription = searchTermFlow.subscribe((term: string) => {
            this.applySearch(term);
            this.changeDetectorRef.markForCheck();
        });
        activityLogger.logActivity(this.actionName);
    }

    public ngOnDestroy() {
        this.searchSubscription.unsubscribe();
    }

    public viewAllReports(viewScheduledReport: boolean = false) {
        return viewScheduledReport ? this.urlBuilder.esa().sampletracking().report().ScheduledReports().navigate() :
            this.urlBuilder.esa().sampletracking().report().GeneratedReports().navigate();
    }

    public getHeaderText() {
        return '';
    }

    public reloadScheduledReports($event: boolean) {
        if (!$event) { return; }
        this.scheduledReportDetails = null;
        this.loadingScheduledReports = false;
        this.loadScheduledReports();
    }

    public reloadGeneratedReports($event: boolean) {
        if (!$event) { return; }
        this.generatedReportDetails = null;
        this.loadingComplete = false;
        this.loadReports();
    }

    private applySearch(term: string) {
        this.generatedReportDetails = null;
        this.scheduledReportDetails = null;
        this.loadingComplete = false;
        this.loadingScheduledReports = false;
        this.generatedReportDetails = this.trackingGridService.dateFormatter(this.reportsColumnService.applySearchToGrid(term, this.generatedReportData, cloneDeep(this.reportsColumnService.generatedReportsHeader)));
        this.scheduledReportDetails = this.trackingGridService.dateFormatter(this.reportsColumnService.applySearchToGrid(term, this.scheduledReportData, cloneDeep(this.reportsColumnService.scheduledReportsHeader)));
        this.loadingComplete = true;
        this.loadingScheduledReports = true;
        this.changeDetectorRef.markForCheck();
    }

    private loadReports() {
        let requestInfo = this.getReportDataRequestInfo();
        forkJoin([
            this.reportsStorageService.getGeneratedReports(requestInfo),
            this.reportsStorageService.getScheduledReports(requestInfo)
        ]).pipe(map(([generatedReports, scheduledReports]) => {
            this.generatedReportDetails = this.trackingGridService.dateFormatter({ header: cloneDeep(this.reportsColumnService.generatedReportsHeader), data: generatedReports });
            this.generatedReportData = this.generatedReportDetails.data;
            this.scheduledReportDetails = this.trackingGridService.dateFormatter({ header: cloneDeep(this.reportsColumnService.scheduledReportsHeader), data: scheduledReports });
            this.scheduledReportData = this.scheduledReportDetails.data;
            this.loadingComplete = true;
            this.loadingScheduledReports = true;
            this.changeDetectorRef.markForCheck();
        })).subscribe();
    }

    private loadScheduledReports() {
        let requestInfo = this.getReportDataRequestInfo();
        this.reportsStorageService.getScheduledReports(requestInfo).subscribe(gridData => {
            this.scheduledReportData = gridData;
            this.scheduledReportDetails = this.trackingGridService.dateFormatter({ header: cloneDeep(this.reportsColumnService.scheduledReportsHeader), data: gridData });
            this.changeDetectorRef.markForCheck();
            this.loadingScheduledReports = true;
        });
    }

    private getActivatedRouteType() {
        this.breadCrumbService.clear();
        let routerUrl: IBreadcrumb = {
            label: 'Reports',
            url: this.urlBuilder.esa().sampletracking().report().home().url()
        };
        this.breadCrumbService.AddRouterToBreadCrumb(routerUrl);
    }

    private getReportDataRequestInfo(): ReportRequestInfo {
        return {
            loadLatestReports: true,
            latestReportsCount: 4,
            actionName: this.actionName
        };
    }
}